<ng-template #template let-anchor>
    <span>{{ anchor.nativeElement.innerText }}</span>
</ng-template>
<div class="h-full" kendoTooltip showOn="none" [tooltipTemplate]="template" (mouseover)="showTooltip($event)">
    <kendo-grid
        class="max-h-full rounded-lg border-0 shadow-md"
        #grid
        id="{{ id }}"
        [data]="gridData"
        [skip]="state.skip"
        [pageable]="pageable"
        [pageSize]="state.take"
        [sortable]="sortable"
        [selectable]="selectable"
        [groupable]="groupable"
        [size]="gridSize"
        [scrollable]="scrollable"
        [reorderable]="reorderable"
        [rowReorderable]="rowReorderable"
        [resizable]="resizable"
        [sort]="state.sort"
        [group]="group"
        [loading]="data?.isLoading || false"
        [kendoGridSelectBy]="selectionKey"
        [filter]="state.filter"
        [rowClass]="rowClass"
        [navigable]="true"
        [(selectedKeys)]="selection"
        (rowReorder)="onRowReorder($event)"
        (dataStateChange)="onDataStateChanged($event)"
        (pageChange)="onPageChange($event)"
        (sortChange)="onSortChange($event)"
        (groupChange)="onGroupChange($event)"
        (selectedKeysChange)="selectedKeysChange.emit($event)"
        (cellClick)="cellClick.emit($event)"
        (cellClose)="cellClose.emit($event)"
        (columnReorder)="onColumnChange()"
        (columnLockedChange)="onColumnChange()"
        (columnVisibilityChange)="onColumnChange()"
        (detailExpand)="onDetailExpanded($event)">
        <ng-template kendoGridToolbarTemplate>
            <ng-content select="[toolbar]" />
            @for (section of buttons; track $index) {
                @if (['Actions', 'Export'].includes(section.label)) {
                    @for (button of section?.items; track $index) {
                        @if (resolveProperty(button?.isVisible)) {
                            <app-button
                                class="pr-2"
                                [variant]="button?.variant"
                                [disabled]="resolveProperty(button?.isDisabled)"
                                [tooltip]="button?.tooltip?.toString() | translate"
                                [icon]="button?.icon"
                                [badgeCount]="resolveProperty(button?.badgeCount)"
                                (clicked)="button?.onClick()">
                                @if (!resolveProperty(button?.iconOnly)) {
                                    <span class="leading-3">
                                        {{ button?.label?.toString() | translate }}
                                    </span>
                                }
                            </app-button>
                        }
                    }
                }
            }
            <kendo-grid-spacer />
            <ng-content select="[toolbarCenter]" />
            <kendo-grid-spacer />
            <ng-content select="[toolbarEnd]" />
            @if (allowGridSave) {
                @if (saveAndLoadButtonsVisible) {
                    <app-button
                        tooltip="{{ 'Save View' | translate }} "
                        variant="success"
                        icon="faSolidCheck"
                        (clicked)="onSaveGridSettings()" />
                }
                @if (savedStateExists && saveAndLoadButtonsVisible) {
                    <app-button
                        tooltip="{{ 'Cancel View Changes' | translate }}"
                        variant="warning"
                        icon="faSolidBan"
                        (clicked)="loadGridSettings()" />
                }
                @if (savedStateExists) {
                    <app-button
                        tooltip="{{ 'Default View' | translate }}"
                        icon="faSolidArrowsRotate"
                        variant="light"
                        (clicked)="clearGridSettings()" />
                }
            }
            @if (showColumnSelector) {
                <app-button class="!p-0" tooltip="{{ 'Columns' | translate }} " variant="light">
                    <kendo-grid-column-chooser [autoSync]="true" />
                </app-button>
            }
            @if (filterable) {
                <app-button
                    tooltip="Filter"
                    icon="faSolidFilter"
                    [variant]="nrOfActiveFilters ? 'primary' : 'light'"
                    (clicked)="toggleFilter.emit()">
                    @if (nrOfActiveFilters) {
                        <span class="leading-3">{{ nrOfActiveFilters }}</span>
                    }
                </app-button>
            }
            @if (nrOfActiveFilters) {
                <app-button variant="light" icon="faSolidXmark" tooltip="Clear Filter" (clicked)="clearFilter.emit()" />
            }
        </ng-template>
        @if (isSelectable && showCheckboxColumn) {
            <kendo-grid-checkbox-column [showSelectAll]="showSelectAll" />
        }
        @if (rowReorderable) {
            <kendo-grid-rowreorder-column [width]="20" />
        }
        @for (column of columnsConfig; track column) {
            @if (column.isVisible) {
                <kendo-grid-column
                    [title]="column.title | translate"
                    [field]="column.field"
                    [width]="column.width"
                    [class]="column.class"
                    [sortable]="column.isSortable"
                    [format]="column.format"
                    [style]="column.style"
                    [hidden]="column.isHidden">
                    @if (column.aggregate) {
                        <ng-template kendoGridGroupFooterTemplate let-aggregates>
                            {{ 'Total' | translate }}:
                            <app-number [value]="aggregates[column.field]?.sum" [format]="column.format" />
                        </ng-template>
                    }
                    @if (column.columnHeaderTooltip) {
                        <ng-template kendoGridHeaderTemplate>
                            <div
                                class="flex w-full items-center justify-between gap-2"
                                kendoTooltip
                                title="{{ column.columnHeaderTooltip.text | translate }}">
                                {{ column.title | translate }}
                                <ng-container *ngIf="column.columnHeaderTooltip.showIcon">
                                    <ng-icon
                                        size="0.75rem"
                                        [class]="
                                            column.columnHeaderTooltip.iconClass || 'text-primary-foreground opacity-75'
                                        "
                                        [name]="column.columnHeaderTooltip.icon || 'faSolidCircleInfo'"></ng-icon>
                                </ng-container>
                            </div>
                        </ng-template>
                    }
                    <ng-template kendoGridCellTemplate let-row="dataItem">
                        @if (column.isLink) {
                            <app-grid-cell-template
                                [column]="column"
                                [defaultViewMode]="defaultViewMode"
                                [parentRoute]="parentRoute"
                                [row]="row" />
                        } @else {
                            @if (column.fieldTooltip) {
                                <app-grid-cell-template
                                    kendoTooltip
                                    title="{{ column.fieldTooltip ? row[column.fieldTooltip] : null }}"
                                    [column]="column"
                                    [row]="row" />
                            } @else {
                                <app-grid-cell-template
                                    [column]="column"
                                    [row]="row"
                                    [ngClass]="column.conditionalClass ? column.conditionalClass(row) : null">
                                    @if (column.indicator && column.indicator(row)) {
                                        <span title="{{ column.indicator(row)?.tooltip | translate }}" kendoTooltip>
                                            @if (column.indicator(row)?.route) {
                                                <app-link
                                                    [route]="[
                                                        column.indicator(row)?.route?.path,
                                                        row[column.indicator(row)?.route?.field]
                                                    ]"
                                                    [queryParams]="column.indicator(row)?.route?.queryParams">
                                                    <ng-icon
                                                        [ngClass]="column.indicator(row)?.class"
                                                        [name]="column.indicator(row)?.icon" />
                                                </app-link>
                                            } @else {
                                                <ng-icon
                                                    [ngClass]="column.indicator(row)?.class"
                                                    [name]="column.indicator(row)?.icon" />
                                            }
                                        </span>
                                    }
                                </app-grid-cell-template>
                            }
                        }
                    </ng-template>
                </kendo-grid-column>
            }
        }
        <kendo-grid-messages
            [pagerItems]="gridMessages.pagerItems | translate"
            [pagerItemsPerPage]="gridMessages.pagerItemsPerPage | translate"
            [noRecords]="gridMessages.noRecords | translate"
            [pagerOf]="gridMessages.pagerOf | translate"
            [groupPanelEmpty]="gridMessages.groupPanelEmpty | translate" />
    </kendo-grid>
</div>
